import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Users, Gift, TrendingUp, Star, Instagram, Music2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CrewJoinPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    instagram_handle: '',
    tiktok_handle: '',
    motivation: '',
    terms_accepted: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.terms_accepted) {
      toast.error('Vous devez accepter les conditions du programme');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/crew/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }
      
      setSuccess(data);
      toast.success(data.message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-zinc-900 to-black py-20 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-green-500" />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            {success.status === 'active' ? 'Bienvenue dans le Crew !' : 'Demande envoyée !'}
          </h1>
          
          <p className="text-zinc-400 mb-8">
            {success.message}
          </p>
          
          {success.promo_code && (
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6 mb-6">
              <p className="text-zinc-400 text-sm mb-2">Ton code promo</p>
              <div className="text-3xl font-bold text-[#C0000C] mb-4">{success.promo_code}</div>
              <p className="text-zinc-400 text-sm mb-2">Ton lien de parrainage</p>
              <div className="bg-black/50 rounded-lg p-3 text-sm text-white font-mono break-all">
                {success.referral_link}
              </div>
            </div>
          )}
          
          <Button
            onClick={() => navigate(success.status === 'active' ? '/crew/dashboard' : '/')}
            className="bg-[#C0000C] hover:bg-[#A0000A] text-white"
          >
            {success.status === 'active' ? 'Accéder à mon dashboard' : 'Retour à la boutique'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-900 to-black">
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#C0000C]/20 border border-[#C0000C]/30 rounded-full px-4 py-2 mb-6">
            <Users className="w-4 h-4 text-[#C0000C]" />
            <span className="text-[#C0000C] text-sm font-medium">Programme d'affiliation</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
            Rejoins le <span className="text-[#C0000C]">Mutagim Crew</span>
          </h1>
          
          <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto">
            Le concept du double 10% : tes followers économisent 10%, 
            et tu gagnes 10% de commission sur chaque vente.
          </p>
          
          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Gift className="w-6 h-6 text-green-500" />
              </div>
              <h3 className="text-white font-bold mb-2">-10% pour tes amis</h3>
              <p className="text-zinc-400 text-sm">Ton code promo donne une réduction exclusive à ta communauté</p>
            </div>
            
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-[#C0000C]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-[#C0000C]" />
              </div>
              <h3 className="text-white font-bold mb-2">+10% pour toi</h3>
              <p className="text-zinc-400 text-sm">Commission sur chaque vente générée par ton code</p>
            </div>
            
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Star className="w-6 h-6 text-yellow-500" />
              </div>
              <h3 className="text-white font-bold mb-2">Jusqu'à 18%</h3>
              <p className="text-zinc-400 text-sm">Monte en niveau et augmente ta commission</p>
            </div>
          </div>
          
          {/* Levels */}
          <div className="bg-zinc-800/20 border border-zinc-700/50 rounded-2xl p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6">Les niveaux du Crew</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: 'Crew', sales: '0-5', rate: '10%', color: 'zinc' },
                { name: 'Crew OG', sales: '6-20', rate: '12%', color: 'blue' },
                { name: 'Crew Elite', sales: '21-50', rate: '15%', color: 'purple' },
                { name: 'Crew Legend', sales: '50+', rate: '18%', color: 'yellow' },
              ].map((level) => (
                <div key={level.name} className={`bg-${level.color}-500/10 border border-${level.color}-500/30 rounded-xl p-4`}>
                  <div className={`text-${level.color}-400 font-bold text-lg`}>{level.name}</div>
                  <div className="text-zinc-400 text-sm">{level.sales} ventes</div>
                  <div className="text-white text-2xl font-black">{level.rate}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Inscription au programme</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-white">Prénom *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                    className="bg-zinc-900/50 border-zinc-700 text-white mt-1"
                    placeholder="Ton prénom"
                    data-testid="crew-join-firstname"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-white">Nom *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                    className="bg-zinc-900/50 border-zinc-700 text-white mt-1"
                    placeholder="Ton nom"
                    data-testid="crew-join-lastname"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email" className="text-white">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="bg-zinc-900/50 border-zinc-700 text-white mt-1"
                  placeholder="ton@email.com"
                  data-testid="crew-join-email"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instagram" className="text-white flex items-center gap-2">
                    <Instagram className="w-4 h-4" /> Instagram
                  </Label>
                  <Input
                    id="instagram"
                    value={formData.instagram_handle}
                    onChange={(e) => setFormData({...formData, instagram_handle: e.target.value})}
                    className="bg-zinc-900/50 border-zinc-700 text-white mt-1"
                    placeholder="@handle"
                    data-testid="crew-join-instagram"
                  />
                </div>
                <div>
                  <Label htmlFor="tiktok" className="text-white flex items-center gap-2">
                    <Music2 className="w-4 h-4" /> TikTok
                  </Label>
                  <Input
                    id="tiktok"
                    value={formData.tiktok_handle}
                    onChange={(e) => setFormData({...formData, tiktok_handle: e.target.value})}
                    className="bg-zinc-900/50 border-zinc-700 text-white mt-1"
                    placeholder="@handle"
                    data-testid="crew-join-tiktok"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="motivation" className="text-white">Pourquoi veux-tu rejoindre le Crew ?</Label>
                <Textarea
                  id="motivation"
                  value={formData.motivation}
                  onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                  className="bg-zinc-900/50 border-zinc-700 text-white mt-1 resize-none"
                  placeholder="Parle-nous de toi et de ta communauté..."
                  rows={3}
                  data-testid="crew-join-motivation"
                />
              </div>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={formData.terms_accepted}
                  onCheckedChange={(checked) => setFormData({...formData, terms_accepted: checked})}
                  className="mt-1 border-zinc-600 data-[state=checked]:bg-[#C0000C] data-[state=checked]:border-[#C0000C]"
                  data-testid="crew-join-terms"
                />
                <Label htmlFor="terms" className="text-zinc-400 text-sm cursor-pointer">
                  J'accepte les conditions du programme d'affiliation Mutagim Crew et 
                  je m'engage à promouvoir la marque de manière éthique. *
                </Label>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C0000C] hover:bg-[#A0000A] text-white py-6 text-lg font-bold"
                data-testid="crew-join-submit"
              >
                {loading ? 'Envoi en cours...' : 'Rejoindre le Mutagim Crew'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CrewJoinPage;
